import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Cancel from '@mui/icons-material/Cancel';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { FormattedMessage } from 'react-intl';
import {
  DATE_FORMAT,
  LoadingEvent,
  useLocalization,
} from '@ui-framework/index';
import {
  getWeekdays,
  IconLibrary,
  useAvailabilitySlotTypes,
} from '@product-framework/index';
import { constantCase } from 'case-anything';
import PermissionButton from '@product-framework/components/PermissionButton';
import PageSection from '@product-framework/components/PageSection';
import FormErrorsArray from '@ui-framework/components/AdvancedForms/FormErrorsArray';
import { useNavigate } from 'react-router-dom';
import DefaultSchedule from '../../utils/DefaultSchedule';
import lib from '../../utils/lib';

function DefaultScheduleDetailView({
  params,
  selectedScheduleId,
  locationId = null,
  serviceProviderId,
}) {
  const navigate = useNavigate();
  const { formatMessage } = useLocalization();
  const { slotTypes } = useAvailabilitySlotTypes();
  const [currentSchedule, setCurrentSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState(null);

  const locationUrl = locationId ? `/locations/${locationId}` : '';

  const initialize = async () => {
    setLoading(true);
    setErrors(false);
    setCurrentSchedule(null);

    const response = await DefaultSchedule.fetch({
      params,
      defaultScheduleId: selectedScheduleId,
      locationId,
    });

    if (response?.ok) {
      const processedData = DefaultSchedule.parseSuccessResponseForNextValue({
        schedule: response?.data,
        slotTypes,
        serviceProviderId: params?.pageId,
        formatMessage,
      });

      setCurrentSchedule(processedData);
    } else {
      setErrors(response?.errors);
    }
    setLoading(false);
  };

  useEffect(() => {
    initialize();
  }, [selectedScheduleId]);

  let componentLoadingOrError = null;

  if (loading) {
    componentLoadingOrError = (
      <LoadingEvent>
        <FormattedMessage
          id="LOADING_DEFAULT_SCHEDULE"
          defaultMessage="Loading Default Schedule"
        />
        ...
      </LoadingEvent>
    );
  }

  if (errors) {
    componentLoadingOrError = (
      <FormErrorsArray
        errors={errors}
        withTitle={false}
        displayAsList={false}
      />
    );
  }

  // 100% / 8 columns
  const width = '12%';

  return (
    <PageSection
      id="ScrollTopElementDefaultScheduleView"
      subSection
      label={formatMessage({
        id: 'VIEW_SCHEDULE',
        defaultMessage: 'View Schedule',
      })}>
      {componentLoadingOrError || (
        <Box sx={{ minHeight: '310px' }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>
              <FormattedMessage
                id="EFFECTIVE_START_DATE"
                defaultMessage="Effective Start Date"
              />
              :
            </Typography>
            <Typography variant="body1">
              {dayjs(currentSchedule?.effectiveStartDate).format(
                DATE_FORMAT.FRIENDLY_WEEKDAY_DATE,
              )}
            </Typography>
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>
              <FormattedMessage
                id="EFFECTIVE_END_DATE"
                defaultMessage="Effective End Date"
              />
              :
            </Typography>
            <Typography variant="body1">
              {currentSchedule?.effectiveEndDate ? (
                dayjs(currentSchedule?.effectiveEndDate).format(
                  DATE_FORMAT.FRIENDLY_WEEKDAY_DATE,
                )
              ) : (
                <FormattedMessage id="INDEFINITE" defaultMessage="Indefinite" />
              )}
            </Typography>
          </Stack>

          <Typography variant="subtitle2" sx={{ flexShrink: 0, mb: 1 }}>
            <FormattedMessage id="SCHEDULE" defaultMessage="Schedule" />:
          </Typography>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <TableContainer>
              <Table
                size="small"
                sx={{
                  borderCollapse: 'collapse',
                  '& .MuiTableCell-root': { border: 'none', py: 1.25, px: 1 },
                }}>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    {getWeekdays(formatMessage).map(wk => (
                      <TableCell key={wk.value} width={width} align="center">
                        <Typography variant="subtitle2">
                          {wk.shortLabel}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {slotTypes.map(sk => (
                    <TableRow key={sk?.id}>
                      <TableCell align="right">
                        <Typography variant="subtitle2">
                          <FormattedMessage
                            id={constantCase(sk?.name)}
                            defaultMessage={sk?.name}
                          />
                        </Typography>
                      </TableCell>
                      {getWeekdays(formatMessage).map(wk => {
                        const slotOn = Boolean(
                          currentSchedule?.schedule?.[wk.value]?.[sk?.code],
                        );
                        let slotContent;
                        if (slotOn) {
                          slotContent = (
                            <CheckCircle
                              fontSize="small"
                              sx={{
                                color: 'success.main',
                                verticalAlign: 'middle',
                              }}
                            />
                          );
                        } else {
                          slotContent = (
                            <Cancel
                              fontSize="small"
                              sx={{
                                color: 'error.main',
                                verticalAlign: 'middle',
                              }}
                            />
                          );
                        }
                        return (
                          <TableCell
                            key={`${wk.value}-${sk?.code}`}
                            align="center">
                            {slotContent}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell align="right">
                      <Typography variant="subtitle2">
                        <FormattedMessage
                          id="CUTOFF_TIME"
                          defaultMessage="Cutoff Time"
                        />
                      </Typography>
                    </TableCell>
                    {getWeekdays(formatMessage).map(wk => (
                      <TableCell key={`${wk.value}-cutoff`} align="center">
                        <Typography variant="body2" color="text.secondary">
                          {currentSchedule?.schedule?.[wk.value]?.cutoff?.time
                            ? lib.formatCutoffSummary(
                                wk.value,
                                currentSchedule?.schedule?.[wk.value]?.cutoff,
                                formatMessage,
                              )
                            : ''}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Stack
              direction="row"
              spacing={1}
              sx={{ justifyContent: 'center', mt: 2 }}>
              <PermissionButton
                permissionName="CAN_UPDATE_SP_AVAILABILITY_DEFAULT_SCHEDULE"
                size="small"
                variant="contained"
                onClick={() => {
                  navigate(
                    `/serviceproviders/edit/${serviceProviderId}${locationUrl}/availability/default-schedules/${selectedScheduleId}/edit`,
                  );
                }}>
                <FormattedMessage
                  id="EDIT_SCHEDULE"
                  defaultMessage="Edit Schedule"
                />
              </PermissionButton>
              <Button
                sx={{ mt: 2, mb: 2 }}
                variant="outlined"
                color="primary"
                startIcon={<IconLibrary.Back />}
                onClick={() => {
                  navigate(
                    `/serviceproviders/edit/${serviceProviderId}${locationUrl}/availability/default-schedules`,
                  );
                }}>
                <FormattedMessage
                  id="BACK_TO_LIST"
                  defaultMessage="Back to List"
                />
              </Button>
            </Stack>
          </Paper>
        </Box>
      )}
    </PageSection>
  );
}

export default DefaultScheduleDetailView;
